/**
 * Pitch geometry, as the server measured it.
 *
 * The catalog's `range` is a nominal 105 x 68 and is a drawing fallback, not
 * the pitch: real dimensions vary by match, and only four of dev's twenty-eight
 * matches are actually 105 x 68. So every spatial judgement here — where the
 * touchline is, whether a bin width tiles the axis, whether two matches can be
 * summed — is made from the `pitch` map the server sends with the match list
 * and with each result. This module knows no pitch dimensions of its own.
 *
 * The arithmetic mirrors `_overrun` and `_tiling_widths` in
 * app/semantic/compiler.py, and is float-tolerant for the same reason: 69 / 11.5
 * is exact only by luck of binary representation, and a width that tiles must
 * not be flagged as one that does not.
 */
import type { PitchDims } from './run'

const EPS = 1e-9

const whole = (n: number): boolean => Math.abs(n - Math.round(n)) < EPS

/** Half-extents, because bins are anchored at 0 and run -half..+half. */
export const halfOf = (pitch: PitchDims, dimension: string): number =>
  (dimension === 'length' ? pitch.length : pitch.width) / 2

/** Does a zero-anchored grid of this width stop exactly on the boundary? */
export const tiles = (half: number, width: number): boolean =>
  width > 0 && whole(half / width)

/** How far past the boundary the band containing it reaches. */
export const overrun = (half: number, width: number): number =>
  tiles(half, width) ? 0 : Math.ceil(half / width) * width - half

/**
 * Bin widths that divide this half-extent exactly, largest first.
 *
 * Whole and half metres only, and between 3 and 12 bands per half — the range
 * a heatmap can be read at. Some half-extents have none: nothing sane tiles
 * 53.5m, and saying so beats offering 1.5m bands.
 */
export function tilingWidths(half: number, limit = 6): number[] {
  const out: number[] = []
  for (let twice = 2; twice <= 80; twice += 1) {
    const w = twice / 2
    const bands = half / w
    if (whole(bands) && Math.round(bands) >= 3 && Math.round(bands) <= 12) out.push(w)
  }
  return out.sort((a, b) => b - a).slice(0, limit)
}

/** The distinct playing surfaces in a selection, each with the matches on it. */
export function shapes(
  pitch: Record<string, PitchDims> | undefined,
  games: string[],
): Array<{ dims: PitchDims; games: string[] }> {
  const by = new Map<string, { dims: PitchDims; games: string[] }>()
  for (const g of games) {
    const dims = pitch?.[g]
    if (!dims) continue
    const key = `${dims.length}x${dims.width}`
    const seen = by.get(key)
    if (seen) seen.games.push(g)
    else by.set(key, { dims, games: [g] })
  }
  return [...by.values()].sort((a, b) => a.dims.length - b.dims.length)
}

/** Matches in the selection whose dimensions the server could not measure. */
export const unmeasured = (
  pitch: Record<string, PitchDims> | undefined,
  games: string[],
): string[] => games.filter((g) => !pitch?.[g])

/** Matches whose dimensions are a default someone wrote down, not a survey. */
export const assumed = (
  pitch: Record<string, PitchDims> | undefined,
  games: string[],
): string[] => games.filter((g) => pitch?.[g]?.assumed)

export const dims = (p: PitchDims): string => `${p.length} x ${p.width}m`
